import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import type { Options, Step } from 'react-joyride';

/**
 * Returns true when the element is actually rendered and visible.
 * Catches `display: none` ancestors (offsetParent === null) and
 * zero-size/detached elements (no client rects).
 */
const isVisible = (element: HTMLElement): boolean =>
  element.offsetParent !== null || element.getClientRects().length > 0;

/**
 * Resolves a CSS selector to the first VISIBLE matching element.
 *
 * Some anchors exist twice in the DOM (e.g. sidebar nav items are rendered
 * in both the mobile temporary drawer and the desktop permanent drawer), so
 * a plain `document.querySelector` could return a hidden twin. This scans
 * all matches and returns the visible one.
 */
export const findVisibleElement = (selector: string): HTMLElement | null => {
  const candidates = document.querySelectorAll<HTMLElement>(selector);
  for (const candidate of candidates) {
    if (isVisible(candidate)) {
      return candidate;
    }
  }
  return null;
};

/**
 * Drops steps whose DOM target is missing or invisible, and converts string
 * selector targets into resolver functions so react-joyride always anchors
 * to the visible element (not the first querySelector match).
 *
 * Steps targeting `'body'` are always kept (centered intro steps).
 */
export const filterValidSteps = (steps: Step[]): Step[] =>
  steps
    .filter((step) => {
      const { target } = step;
      if (target === 'body') {
        return true;
      }
      if (typeof target === 'string') {
        return findVisibleElement(target) !== null;
      }
      return true;
    })
    .map((step) => {
      const { target } = step;
      if (typeof target === 'string' && target !== 'body') {
        return { ...step, target: () => findVisibleElement(target) };
      }
      return step;
    });

/**
 * Shared react-joyride v3 options derived from the active MUI theme.
 *
 * In react-joyride v3 the colors/z-index that used to live in
 * `styles.options` moved into the `options` prop, so this returns
 * `Partial<Options>` for the Joyride `options` prop. Tooltip visuals come
 * from the custom TourTooltip component; these options only style the
 * overlay, spotlight arrow, and stacking order.
 */
export const useJoyrideOptions = (): Partial<Options> => {
  const theme = useTheme();

  return useMemo(
    () => ({
      arrowColor: theme.palette.background.paper,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      primaryColor: theme.palette.primary.main,
      zIndex: theme.zIndex.modal + 1,
      // v3 shows a pulsing beacon before each tooltip by default; open
      // tooltips directly like a classic guided tour instead.
      skipBeacon: true,
    }),
    [theme]
  );
};
